import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/apiClient.ts";

const LEDGER_URL = "/v1/reports/travelers-ledger";
const LIST_URL = "/v1/reports/travelers-list";

function classAmount(n) {
  if (n < 0) return "text-red-600";
  if (n > 0) return "text-green-700";
  return "";
}

const TravelersLedger = () => {
  // ----- فلاتر -----
  const [travelerId, setTravelerId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [df, setDf] = useState(""); // YYYY-MM-DD
  const [dt, setDt] = useState("");
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);

  // ----- بيانات المسافرين (للـ ListBox) -----
  const [travQuery, setTravQuery] = useState(""); // للبحث عن المسافرين
  const [travelers, setTravelers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // ----- بيانات الكشف -----
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const canFetch = useMemo(() => travelerId && String(travelerId).trim().length > 0, [travelerId]);

  // جلب قائمة المسافرين (يدعم ?q=)
  const fetchTravelers = async () => {
    setListLoading(true);
    try {
      const { data } = await api.get(LIST_URL, {
        params: travQuery ? { q: travQuery } : {},
      });
      setTravelers(data?.data || []);
    } catch (e) {
      console.error("Failed to load travelers", e);
    } finally {
      setListLoading(false);
    }
  };

  // جلب مجموعات المسافر المختار
  const fetchTravelerGroups = async (trId) => {
    if (!trId) {
      setGroups([]);
      return;
    }
    setGroupsLoading(true);
    try {
      const { data } = await api.get(`${LEDGER_URL}/groups`, {
        params: { traveler_id: Number(trId) },
      });
      setGroups(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error("Failed to load traveler groups", e);
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  };

  // أول تحميل لقائمة المسافرين
  useEffect(() => {
    fetchTravelers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // إعادة تحميل القائمة عند تغيير نص البحث (تقدر تضيف debounce لو تحب)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchTravelers();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travQuery]);

  useEffect(() => {
    setGroupId("");
    fetchTravelerGroups(travelerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelerId]);

  // جلب الكشف
  const fetchLedger = async () => {
    if (!canFetch) {
      setResp(null);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const params = {
        traveler_id: Number(travelerId),
        per_page: perPage,
        page,
      };
      if (groupId) params.gr_id = Number(groupId);
      if (df) params.df = df; // الباك ينظّف 00:00:00
      if (dt) params.dt = dt; // 23:59:59

      const { data } = await api.get(LEDGER_URL, { params });
      setResp(data);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Network error while fetching ledger";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };
    
  const handleDownloadPDF = async () => {
    try {
      if (!travelerId) return alert("اختار المسافر أولاً");

      const params = new URLSearchParams({
        traveler_id: travelerId,
        gr_id: groupId || "",
        df: df || "",
        dt: dt || "",
        format: "pdf",
      });

      // استخدم نفس apiClient حتى يمرر X-Tenant و X-Branch و Authorization
      const response = await api.get(`/v1/reports/travelers-ledger?${params.toString()}`, {
        responseType: "blob", // مهم جداً
      });

      // إنشاء رابط تحميل مؤقت
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // إنشاء عنصر <a> مخفي
      const a = document.createElement("a");
      a.href = url;
      a.download = `TravelerLedger_${travelerId}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();

      // تنظيف
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download error:", e);
      alert("فشل تحميل التقرير PDF");
    }
  };


  // جلب الكشف عند تغيّر الفلاتر
  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelerId, groupId, df, dt, perPage, page]);

  // إعادة الصفحة 1 عند تغيّر فلاتر أساسية
  useEffect(() => {
    setPage(1);
  }, [travelerId, groupId, df, dt, perPage]);


function formatDateOnly(datetime) {
  if (!datetime) return "";
  return datetime.split(" ")[0]; // ترجع الجزء قبل الفراغ (التاريخ فقط)
}

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-semibold">كشف حساب المسافر</h1>

      {/* فلاتر أعلى */}
      <div className="grid grid-cols-1 md:grid-cols-8 gap-3 items-end">
        {/* البحث عن مسافر */}
        <div className="md:col-span-2 flex flex-col">
          <label className="text-sm mb-1">بحث مسافر (الاسم/الجواز/الهاتف)</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={travQuery}
              onChange={(e) => setTravQuery(e.target.value)}
              className="border rounded-md px-3 py-2 w-full"
              placeholder="اكتب للبحث..."
            />
            <button
              onClick={fetchTravelers}
              className="px-3 py-2 rounded-md border"
              disabled={listLoading}
            >
              بحث
            </button>
          </div>
        </div>

        {/* قائمة المسافرين */}
        <div className="md:col-span-2 flex flex-col">
          <label className="text-sm mb-1">المسافر</label>
          <select
            value={travelerId}
            onChange={(e) => setTravelerId(e.target.value)}
            className="border rounded-md px-3 py-2"
          >
            <option value="">-- اختر مسافر --</option>
            {travelers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} {t.passport ? `(${t.passport})` : ""}
              </option>
            ))}
          </select>
          {listLoading && <span className="text-xs text-gray-500 mt-1">جاري تحميل القائمة...</span>}
        </div>

        {/* قائمة المجموعات الخاصة بالمسافر (اختيارية) */}
        <div className="md:col-span-2 flex flex-col">
          <label className="text-sm mb-1">المجموعة (اختياري)</label>
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            className="border rounded-md px-3 py-2"
            disabled={!travelerId || groupsLoading}
          >
            <option value="">-- كل المجموعات --</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name || `#${g.id}`}
              </option>
            ))}
          </select>
          {groupsLoading && <span className="text-xs text-gray-500 mt-1">جاري تحميل المجموعات...</span>}
        </div>

        {/* df */}
        <div className="flex flex-col">
          <label className="text-sm mb-1">من تاريخ</label>
          <input
            type="date"
            value={df}
            onChange={(e) => setDf(e.target.value)}
            className="border rounded-md px-3 py-2"
          />
        </div>

        {/* dt */}
        <div className="flex flex-col">
          <label className="text-sm mb-1">إلى تاريخ</label>
          <input
            type="date"
            value={dt}
            onChange={(e) => setDt(e.target.value)}
            className="border rounded-md px-3 py-2"
          />
        </div>

        {/* per page + أزرار */}
        <div className="flex flex-col">
          <label className="text-sm mb-1">لكل صفحة</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-md px-3 py-2"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="flex gap-2 md:col-span-2">
          <button
            onClick={fetchLedger}
            disabled={!canFetch || loading}
            className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
          >
            {loading ? "يرجى الانتظار..." : "تحميل البيانات"}
          </button>
          <button
            onClick={() => {
              setTravelerId("");
              setGroupId("");
              setGroups([]);
              setDf("");
              setDt("");
              setPerPage(50);
              setPage(1);
              setResp(null);
              setErr(null);
            }}
            className="px-4 py-2 rounded-md border"
          >
            تفريغ
          </button>
            {/* 🔹 زر تحميل PDF */}
          {travelerId && (
            <button
              onClick={() => handleDownloadPDF()}
              className="px-4 py-2 rounded-md bg-green-600 text-white"
            >
              تحميل PDF
            </button>
          )}
        </div>
      </div>

      {/* رسائل */}
      {err && <div className="text-red-600">⚠️ {err}</div>}
      {!err && !loading && !resp && (
        <div className="text-gray-600">اختر المسافر ثم اضغط تحميل.</div>
      )}

      {/* معلومات عامة */}
      {resp && (
        <div className="text-sm text-gray-700">
          <div>
            الفترة: <b>{formatDateOnly(resp.date_from)}</b> ← <b>{formatDateOnly(resp.date_to)}</b>
          </div>
          <div>
            السجلات: <b>{resp.total}</b> | صفحة <b>{resp.current_page}</b> من{" "}
            <b>{resp.last_page}</b>
          </div>
        </div>
      )}

      {/* جدول */}
      {resp && resp.data && resp.data.length > 0 && (
        <div className="app-table-wrap">
          <table className="app-table min-w-full border">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">التاريخ</th>
                <th className="p-2 border">المجموعة</th>
                <th className="p-2 border">النوع</th>
                <th className="p-2 border">المبلغ</th>
                <th className="p-2 border">رصيد المجموعة</th>
                <th className="p-2 border">الرصيد </th>
                <th className="p-2 border"> الملاحظات </th>
              </tr>
            </thead>
            <tbody>
              {resp.data.map((r, idx) => (
                <tr key={`${r.updated_at}-${r.entry_type}-${r.source_id || idx}`}>
                  <td className="p-2 border whitespace-nowrap">{r.updated_at}</td>
                  <td className="p-2 border">{r.group_name || "-"}</td>
                  <td className="p-2 border">
                    {r.entry_type === "deal" ? "اتفاق/سعر" : "قسط مسدد"}
                  </td>
                  <td className={`p-2 border text-right tabular-nums ${classAmount(r.amount)}`}>
                    {Number(r.amount).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="p-2 border text-right tabular-nums font-semibold text-blue-700">
                    {Number(r.group_balance || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="p-2 border text-right tabular-nums font-semibold">
                    {Number(r.cumulative_balance).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="p-2 border">{r.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {resp && (
        <div className="flex flex-wrap gap-4 text-sm">
          <div>الرصيد السابق: <b>{Number(resp.opening_balance || 0).toLocaleString()}</b></div>
          <div>إجمالي حركة الفترة: <b>{Number(resp.period_total || 0).toLocaleString()}</b></div>
          <div>الرصيد النهائي ({resp.ending_balance_as_of}): <b>{Number(resp.ending_balance || 0).toLocaleString()}</b></div>
        </div>
      )}

      {/* Pagination */}
      {resp && resp.last_page > 1 && (
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-2 border rounded disabled:opacity-50"
            disabled={resp.current_page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            السابق
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: resp.last_page })
              .slice(Math.max(0, resp.current_page - 3), resp.current_page + 2)
              .map((_, i) => {
                const start = Math.max(1, resp.current_page - 2);
                const pageNum = start + i;
                if (pageNum > resp.last_page) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-2 rounded border ${
                      pageNum === resp.current_page ? "bg-blue-600 text-white" : ""
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
          </div>

          <button
            className="px-3 py-2 border rounded disabled:opacity-50"
            disabled={resp.current_page >= resp.last_page || loading}
            onClick={() => setPage((p) => Math.min(resp.last_page, p + 1))}
          >
            التالي
          </button>
        </div>
      )}
    </div>
  );
};

export default TravelersLedger;
